import type { PriceList } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/types";
import useCurrencyFormatter from "$routes/patient/[:patientId]/_lib/invoices/hooks/useCurrencyFormatter";
import { printV2Receipt } from "$routes/patient/[:patientId]/_lib/printing/print-v2-receipt";
import useThermalPrinter from "$routes/patient/[:patientId]/_lib/printing/use-thermal-printer";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useFetchPrintableInvoiceRxdb } from "./use-fetch-printable-invoice-rxdb";

function patchCurrencyFormatter(
  currencyFormatter: (amountInPence: number) => string,
): {
  format: (amountInPence: number) => string;
} {
  // transcodes UTF-8 currency symbols to be compatible with thermal printers
  return {
    format: (amountInPence) =>
      currencyFormatter(amountInPence).replace(/₵/g, "¢"),
  };
}

export function usePrintThermalReceiptRxdb(
  syncInvoiceId: string,
  priceList: PriceList | null,
) {
  const intl = useIntl();
  const { currencyFormatter } = useCurrencyFormatter();
  const {
    print,
    havePrinter,
    reset: resetPrinter,
    error: printerError,
  } = useThermalPrinter();
  const [fetchError, setFetchError] = useState<Error>(null);

  // Invoice data is reactive from rxdb — no Apollo refetch needed.
  const { invoice } = useFetchPrintableInvoiceRxdb(syncInvoiceId, priceList);

  const resetErrors = () => {
    resetPrinter();
    setFetchError(null);
  };

  const printReceipt = async () => {
    if (!invoice) {
      setFetchError(new Error("Invoice not available in local store"));
      return;
    }
    try {
      await print((printer) =>
        printV2Receipt({
          invoice,
          printer,
          currencyFormatter: patchCurrencyFormatter(currencyFormatter),
          penceToPounds: (i: number) => i,
          intl,
        }),
      );
    } catch (e) {
      setFetchError(e instanceof Error ? e : new Error(String(e)));
    }
  };

  return { havePrinter, printReceipt, resetErrors, fetchError, printerError };
}
